import React, { useCallback, useEffect, useMemo } from 'react';
import { generateShortId, isFunction } from '@agilepoint/utils-common';
import styled, { FlattenSimpleInterpolation } from 'styled-components';
import { Popup } from './popup';
import { IPopupProps, IWithPopupProps, IPopupQueueItem } from './popup-props';
import { createPopupEventManager, PopupEventManagerContext } from './popup-event-context';
import { useQueue } from '../../hooks';

const getStyledPopup = (cssStyles?: FlattenSimpleInterpolation) => (cssStyles ? styled(Popup)`${cssStyles}` : Popup);

export const WithPopup = (props: IWithPopupProps) => {
    const { eventManager: eventManagerFromProps, onDismiss } = props;
    const eventManager = useMemo(() => eventManagerFromProps || createPopupEventManager(), [eventManagerFromProps]);
    const { clear, currentItem, dequeue, enqueue } = useQueue<IPopupProps & { id: string, cssStyles?: FlattenSimpleInterpolation }>();

    const createPopup = useCallback((popupProps: IPopupProps) => {
        enqueue({
            ...popupProps,
            id: popupProps.id || generateShortId()
        });
    }, [enqueue]);

    const removePopup = useCallback((id?: React.Key) => {
        if (isFunction(onDismiss)) {
            onDismiss(id ?? currentItem?.id);
        }
        if (currentItem && isFunction(currentItem.onDismiss)) {
            currentItem.onDismiss();
        }
        if (id === undefined) {
            clear();
            return;
        }
        dequeue(id);
    }, [clear, currentItem, dequeue, onDismiss]);

    useEffect(() => {
        eventManager.subscribe('showPopup', createPopup);
        eventManager.subscribe('removePopup', removePopup);
        return () => {
            eventManager.unsubscribe('showPopup', createPopup);
            eventManager.unsubscribe('removePopup', removePopup);
        };
    }, [createPopup, eventManager, removePopup]);

    const currentItemProps = useMemo(() => (
        currentItem === null
            ? {}
            : {
                ...currentItem,
                key: currentItem.id,
                onDismiss: () => removePopup(currentItem.id)
            } as IPopupQueueItem
    ), [removePopup, currentItem]);

    return (
        <>
            <PopupEventManagerContext.Provider value={eventManager}>
                {props.children}
            </PopupEventManagerContext.Provider>
            {
                currentItem === null
                    ? null
                    : React.createElement(getStyledPopup(currentItem.cssStyles), { ...props, ...currentItemProps })
            }
        </>
    );
};

WithPopup.displayName = 'WithPopup';
